import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, CardContent, ConfirmDialog, Input } from '@mlm/ui';
import { buildTrackingUrl } from '@mlm/shared';
import {
  courierLogoUrl,
  fulfillOrder,
  getCourierLabels,
  getMerchantMe,
  getMerchantOrder,
  listOrders,
  type CourierLabel,
  type MerchantOrder,
  type MerchantOrderDetail,
} from '../lib/api';
import { CopyButton } from '../components/copy-button';

/** Inline truck glyph (merchant app avoids an icon dependency). */
function TruckIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M14 18V6a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h2" />
      <path d="M14 9h4l4 4v4a1 1 0 0 1-1 1h-1" />
      <circle cx="7" cy="18" r="2" />
      <circle cx="17" cy="18" r="2" />
    </svg>
  );
}

function orderStatusVariant(status: string): 'success' | 'destructive' | 'secondary' {
  if (status === 'completed' || status === 'delivered') return 'success';
  if (status === 'cancelled' || status === 'refunded') return 'destructive';
  return 'secondary';
}

// The next fulfillment action a merchant can take, by current status.
const NEXT: Record<string, { status: string; label: string; needsTracking?: boolean } | undefined> =
  {
    paid: { status: 'to_ship', label: 'Mark ready to ship' },
    to_ship: { status: 'shipping', label: 'Ship', needsTracking: true },
    shipping: { status: 'delivered', label: 'Mark delivered' },
  };

// Statuses where the merchant still has to physically pack/ship the goods.
const NEEDS_PREP = new Set(['paid', 'to_ship']);

type ShipToAddress = {
  fullName?: string;
  phone?: string;
  line1?: string;
  barangay?: string;
  city?: string;
  province?: string;
  region?: string;
  postalCode?: string;
} & Record<string, unknown>;

function formatAddressLines(a: ShipToAddress | null | undefined): string[] {
  if (!a) return [];
  const lines: string[] = [];
  if (a.line1) lines.push(String(a.line1));
  const second = [a.barangay, a.city]
    .map((p) => p && String(p).trim())
    .filter(Boolean)
    .join(', ');
  if (second) lines.push(second);
  const third = [a.province, a.region]
    .map((p) => p && String(p).trim())
    .filter(Boolean)
    .join(', ');
  if (third) lines.push(third);
  if (a.postalCode) lines.push(String(a.postalCode));
  return lines;
}

const selectCls =
  'h-9 rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function OrdersPage() {
  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['merchant-orders'],
    queryFn: () => listOrders(),
  });
  const { data: couriers = [] } = useQuery({
    queryKey: ['courier-labels'],
    queryFn: getCourierLabels,
  });
  const courierById = new Map(couriers.map((c) => [c.id, c]));
  const courierFor = (id: string | null) => (id ? (courierById.get(id) ?? null) : null);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [courierFilter, setCourierFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  // Statuses actually present, so the dropdown never offers an empty option.
  const statuses = useMemo(() => [...new Set(orders.map((o) => o.status))].sort(), [orders]);
  const hasFilters = !!(search || status || courierFilter || from || to);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const fromT = from ? new Date(`${from}T00:00:00`).getTime() : null;
    const toT = to ? new Date(`${to}T23:59:59.999`).getTime() : null;
    return orders.filter((o) => {
      if (status && o.status !== status) return false;
      if (courierFilter && (o.courierId ?? '') !== courierFilter) return false;
      const t = new Date(o.createdAt).getTime();
      if (fromT !== null && t < fromT) return false;
      if (toT !== null && t > toT) return false;
      if (term) {
        const hay = [o.code, o.trackingNumber, o.productNames, o.buyerName]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [orders, search, status, courierFilter, from, to]);

  function clearFilters() {
    setSearch('');
    setStatus('');
    setCourierFilter('');
    setFrom('');
    setTo('');
  }

  return (
    <div className="max-w-4xl space-y-4">
      <h1 className="text-2xl font-semibold">Orders</h1>

      {orders.length > 0 && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[12rem] flex-1">
            <label className="mb-1 block text-xs text-muted-foreground">Search</label>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tracking #, product, or customer name"
              className="h-9"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">From</label>
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="h-9 w-[9.5rem]"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">To</label>
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="h-9 w-[9.5rem]"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className={selectCls}
            >
              <option value="">All statuses</option>
              {statuses.map((s) => (
                <option key={s} value={s}>
                  {s.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Courier</label>
            <select
              value={courierFilter}
              onChange={(e) => setCourierFilter(e.target.value)}
              className={selectCls}
            >
              <option value="">All couriers</option>
              {couriers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          {hasFilters && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9"
              onClick={clearFilters}
            >
              Clear
            </Button>
          )}
        </div>
      )}

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : orders.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            No orders yet.
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            No orders match your filters.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-muted-foreground">
                <tr>
                  <th className="p-3">Order</th>
                  <th className="p-3">Courier</th>
                  <th className="p-3">Total</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <OrderRow key={o.id} order={o} courier={courierFor(o.courierId)} />
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/** Courier logo + name as a clear chip (logo falls back to text on load error). */
function CourierChip({ courier }: { courier: CourierLabel | null }) {
  if (!courier) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border bg-muted/40 px-2 py-0.5 text-xs font-medium">
      {courier.logoKey && (
        <img
          src={courierLogoUrl(courier.id)}
          alt=""
          className="h-4 w-auto"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
      )}
      {courier.label}
    </span>
  );
}

function OrderRow({ order, courier }: { order: MerchantOrder; courier: CourierLabel | null }) {
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const next = NEXT[order.status];

  return (
    <>
      <tr className="border-b last:border-0 hover:bg-muted/30">
        <td className="p-3">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-1.5 text-left"
            aria-expanded={open}
          >
            <span className="text-xs text-muted-foreground">{open ? '▾' : '▸'}</span>
            <span>
              <span className="font-medium">{order.code}</span>
              {order.buyerName && (
                <span className="block text-xs text-muted-foreground">{order.buyerName}</span>
              )}
              <span className="block text-xs text-muted-foreground">
                {new Date(order.createdAt).toLocaleDateString()}
              </span>
            </span>
          </button>
        </td>
        <td className="p-3">
          <CourierChip courier={courier} />
        </td>
        <td className="p-3 tabular-nums">₱{Number(order.total).toFixed(2)}</td>
        <td className="p-3">
          <Badge variant={orderStatusVariant(order.status)} className="capitalize">
            {order.status.replace(/_/g, ' ')}
          </Badge>
        </td>
        <td className="p-3">
          {next ? (
            <Button size="sm" onClick={() => setConfirmOpen(true)}>
              {next.label}
            </Button>
          ) : order.status === 'completed' ? (
            <span className="text-xs text-muted-foreground">Done</span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </td>
      </tr>
      {open && (
        <tr className="border-b last:border-0 bg-muted/20">
          <td colSpan={5} className="p-3">
            <OrderDetail orderId={order.id} status={order.status} courier={courier} />
          </td>
        </tr>
      )}
      {next && (
        <ShipConfirmDialog
          order={order}
          next={next}
          courier={courier}
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
        />
      )}
    </>
  );
}

/**
 * Confirm a fulfillment transition after reviewing the order's items (mirrors
 * the admin Orders confirmation). The Ship step (to_ship → shipping) takes the
 * courier tracking number inside the dialog.
 */
function ShipConfirmDialog({
  order,
  next,
  courier,
  open,
  onOpenChange,
}: {
  order: MerchantOrder;
  next: { status: string; label: string; needsTracking?: boolean };
  courier: CourierLabel | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const [tracking, setTracking] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const detailQ = useQuery({
    queryKey: ['merchant-order', order.id],
    queryFn: () => getMerchantOrder(order.id),
    enabled: open,
  });
  const mut = useMutation({
    meta: { successMessage: 'Order updated.' },
    mutationFn: () =>
      fulfillOrder(order.id, { status: next.status, trackingNumber: tracking || undefined }),
    onSuccess: () => {
      setErr(null);
      setTracking('');
      qc.invalidateQueries({ queryKey: ['merchant-orders'] });
      onOpenChange(false);
    },
    onError: (e: unknown) => {
      const data = (e as { response?: { data?: { error?: string; existingOrderCode?: string } } })
        ?.response?.data;
      if (data?.error === 'tracking_number_in_use') {
        setErr(
          data.existingOrderCode
            ? `Tracking # already used on order ${data.existingOrderCode}.`
            : 'That tracking # is already used on another order.',
        );
      } else {
        setErr('Could not update the order. Please try again.');
      }
    },
  });

  const items = detailQ.data?.items ?? [];
  const title = next.needsTracking ? `Ship order ${order.code}` : `${next.label} · ${order.code}`;
  const description = next.needsTracking
    ? 'Confirm the items below, enter the courier tracking number, then ship.'
    : 'Confirm the items to prepare for this order.';

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setTracking('');
          setErr(null);
        }
        onOpenChange(o);
      }}
      title={title}
      description={description}
      confirmLabel={next.needsTracking ? 'Ship' : next.label}
      errorMessage={err}
      loading={mut.isPending}
      confirmDisabled={next.needsTracking ? !tracking.trim() : false}
      onConfirm={async () => {
        await mut.mutateAsync();
      }}
    >
      <div className="space-y-3">
        {courier && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Courier:</span>
            <CourierChip courier={courier} />
          </div>
        )}
        {detailQ.isLoading ? (
          <p className="text-xs text-muted-foreground">Loading items…</p>
        ) : items.length > 0 ? (
          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">
              Ordered items ({items.reduce((s, it) => s + it.qty, 0)})
            </div>
            <div className="max-h-44 divide-y overflow-y-auto rounded-md border">
              {items.map((it) => (
                <div
                  key={it.id}
                  className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm"
                >
                  <span className="min-w-0 truncate">
                    {it.productName ?? 'Product'}
                    {it.variantName ? ` · ${it.variantName}` : ''}
                  </span>
                  <span className="shrink-0 font-mono text-xs text-muted-foreground tabular-nums">
                    ×{it.qty} · ₱{Number(it.unitPrice).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {next.needsTracking && (
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Tracking number</label>
            <Input
              value={tracking}
              onChange={(e) => {
                setTracking(e.target.value);
                setErr(null);
              }}
              placeholder="e.g. JT0123456789"
              autoFocus
            />
          </div>
        )}
      </div>
    </ConfirmDialog>
  );
}

/** Expanded view: the products to prepare for this order + a printable packing slip. */
function OrderDetail({
  orderId,
  status,
  courier,
}: {
  orderId: string;
  status: string;
  courier: CourierLabel | null;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['merchant-order', orderId],
    queryFn: () => getMerchantOrder(orderId),
  });

  if (isLoading || !data) return <p className="text-xs text-muted-foreground">Loading items…</p>;

  const courierLabel = courier?.label ?? null;
  const trackingUrl = buildTrackingUrl(courier?.trackingUrlTemplate, data.trackingNumber);

  return (
    <div className="space-y-3">
      {data.trackingNumber && (
        <div className="flex items-center gap-1.5 rounded-md border bg-background px-3 py-2 text-xs">
          <TruckIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="text-muted-foreground">Tracking #:</span>
          {trackingUrl ? (
            <a
              href={trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono font-medium text-primary underline underline-offset-2"
            >
              {data.trackingNumber}
            </a>
          ) : (
            <span className="font-mono font-medium">{data.trackingNumber}</span>
          )}
          <CopyButton
            value={data.trackingNumber}
            label="Tracking number copied"
            title="Copy tracking number"
          />
          {trackingUrl && (
            <Button asChild size="sm" variant="outline" className="ml-auto h-7 gap-1.5">
              <a href={trackingUrl} target="_blank" rel="noopener noreferrer">
                <TruckIcon className="h-3.5 w-3.5" />
                Track Package
              </a>
            </Button>
          )}
        </div>
      )}
      <div>
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {NEEDS_PREP.has(status) ? 'To prepare' : 'Items'}
        </div>
        <table className="w-full text-sm">
          <tbody>
            {data.items.map((it) => (
              <tr key={it.id} className="border-t first:border-0">
                <td className="py-1.5 pr-2 w-10">
                  {it.productImages?.[0] ? (
                    <img
                      src={it.productImages[0]}
                      alt=""
                      className="h-8 w-8 rounded object-cover"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded bg-muted" />
                  )}
                </td>
                <td className="py-1.5 pr-2">
                  <div className="font-medium">{it.productName ?? 'Product'}</div>
                  {it.variantName && (
                    <div className="text-xs text-muted-foreground">{it.variantName}</div>
                  )}
                  {it.productSku && (
                    <div className="text-[11px] font-mono text-muted-foreground">
                      {it.productSku}
                    </div>
                  )}
                </td>
                <td className="py-1.5 pr-2 text-right font-semibold tabular-nums">×{it.qty}</td>
                <td className="py-1.5 pl-2 text-right tabular-nums text-muted-foreground">
                  ₱{Number(it.unitPrice).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PackingSlip order={data} courierLabel={courierLabel} />
    </div>
  );
}

/**
 * Printable packing slip (mirrors the admin one). The `.packing-slip` element is
 * the only thing that survives `@media print` (see index.css). Ship-to + a
 * prominent qty column for the packer; ship-from is the merchant's own store.
 */
function PackingSlip({
  order,
  courierLabel,
}: {
  order: MerchantOrderDetail;
  courierLabel: string | null;
}) {
  const { data: me } = useQuery({ queryKey: ['merchant-me'], queryFn: getMerchantMe });
  const address = order.shipToAddress as ShipToAddress | null;
  const addressLines = formatAddressLines(address);
  const buyerName = order.buyerFullName || address?.fullName || '';
  const phone = address?.phone || order.buyerPhone || '';

  return (
    <div>
      <div className="no-print flex justify-end">
        <Button size="sm" variant="outline" onClick={() => window.print()}>
          Print packing slip
        </Button>
      </div>

      <div className="packing-slip mt-2 rounded-md border bg-background p-6 text-foreground">
        <header className="flex items-start justify-between gap-6 border-b pb-4">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">PACKING SLIP</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {new Date(order.createdAt).toLocaleString()}
            </p>
          </div>
          <div className="text-right">
            <p className="font-mono text-sm">{order.code}</p>
          </div>
        </header>

        <section className="grid grid-cols-2 gap-6 border-b py-5">
          <div>
            <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
              Ship to
            </p>
            {buyerName && <p className="text-base font-semibold leading-tight">{buyerName}</p>}
            {addressLines.length ? (
              addressLines.map((line, i) => (
                <p key={i} className="text-sm leading-snug">
                  {line}
                </p>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No shipping address on file.</p>
            )}
            {phone && <p className="mt-1 text-sm">☎ {phone}</p>}
          </div>
          <div>
            <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
              Ship from
            </p>
            <p className="text-base font-semibold leading-tight">
              {me?.merchant.businessName ?? 'Your store'}
            </p>
            {courierLabel && (
              <p className="mt-2 text-xs">
                <span className="text-muted-foreground">Courier: </span>
                <span>{courierLabel}</span>
              </p>
            )}
            {order.trackingNumber && (
              <p className="mt-2 text-xs">
                <span className="text-muted-foreground">Tracking: </span>
                <span className="font-mono">{order.trackingNumber}</span>
              </p>
            )}
          </div>
        </section>

        <table className="mt-4 w-full text-sm">
          <thead className="text-left text-muted-foreground">
            <tr className="border-b">
              <th className="py-2 pr-2 font-medium">Item</th>
              <th className="py-2 pr-2 text-right font-medium">Qty</th>
            </tr>
          </thead>
          <tbody>
            {order.items.map((it) => (
              <tr key={it.id} className="border-b last:border-0">
                <td className="py-2 pr-2">
                  <div className="font-medium">{it.productName ?? 'Product'}</div>
                  {it.variantName && (
                    <div className="text-xs text-muted-foreground">{it.variantName}</div>
                  )}
                  {it.productSku && (
                    <div className="text-[11px] font-mono text-muted-foreground">
                      {it.productSku}
                    </div>
                  )}
                </td>
                <td className="py-2 pr-2 text-right text-lg font-bold tabular-nums">{it.qty}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
